import React from 'react';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  RefreshCw, 
  Wifi, 
  WifiOff,
  Zap
} from 'lucide-react';

const SyncStateBadge = ({ 
  state = 'synced', // synced, pending, conflict, error, optimistic, offline
  version = null,
  serverVersion = null,
  className = '',
  size = 'sm',
  onResolveConflict = null 
}) => {
  const getBadgeConfig = () => {
    switch (state) {
      case 'synced':
        return {
          variant: 'outline',
          className: 'bg-green-50 text-green-700 border-green-200',
          icon: <CheckCircle className="w-3 h-3" />,
          text: 'Synced',
          tooltip: 'All changes are synchronized'
        };
        
      case 'pending':
        return {
          variant: 'outline', 
          className: 'bg-yellow-50 text-yellow-700 border-yellow-200 animate-pulse',
          icon: <Clock className="w-3 h-3" />,
          text: 'Syncing...',
          tooltip: 'Changes are being synchronized'
        };
        
      case 'optimistic':
        return {
          variant: 'outline',
          className: 'bg-blue-50 text-blue-700 border-blue-200',
          icon: <Zap className="w-3 h-3" />,
          text: 'Updating',
          tooltip: 'Optimistic update applied, waiting for server confirmation'
        };
        
      case 'conflict':
        return {
          variant: 'destructive',
          className: 'bg-red-50 text-red-700 border-red-300 animate-pulse',
          icon: <AlertTriangle className="w-3 h-3" />,
          text: 'Conflict',
          tooltip: `Version conflict detected. Your version: ${version}, Server version: ${serverVersion}`
        };
        
      case 'error':
        return {
          variant: 'destructive',
          className: 'bg-red-50 text-red-700 border-red-300',
          icon: <RefreshCw className="w-3 h-3" />,
          text: 'Error',
          tooltip: 'Sync failed. Click to retry.'
        };
        
      case 'offline':
        return {
          variant: 'outline',
          className: 'bg-gray-50 text-gray-700 border-gray-300',
          icon: <WifiOff className="w-3 h-3" />,
          text: 'Offline',
          tooltip: 'Working offline. Changes will sync when connection is restored.'
        };
        
      default:
        return {
          variant: 'outline',
          className: 'bg-gray-50 text-gray-700 border-gray-300',  
          icon: <Wifi className="w-3 h-3" />,
          text: 'Unknown',
          tooltip: 'Unknown sync state'
        };
    }
  };

  const config = getBadgeConfig();

  const handleClick = () => {
    if (state === 'conflict' && onResolveConflict) {
      onResolveConflict();
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge 
            variant={config.variant}
            className={`${config.className} ${className} ${state === 'conflict' ? 'cursor-pointer hover:bg-red-100' : ''}`}
            onClick={handleClick}
          >
            {config.icon}
            <span className="ml-1 text-xs">{config.text}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="max-w-xs">
            <p>{config.tooltip}</p>
            {state === 'conflict' && onResolveConflict && (
              <p className="mt-1 text-xs text-gray-500">
                Click to resolve conflict
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SyncStateBadge;